/**
 * **C07 · The raffle and bingo statement covers** (Stream C, Documents.dc.html): two half-letter
 * covers laid out by the `gaming_covers` engine from Money's state filings and the raffle books
 * (Marketing). Scored against the drawing by walk 99g.
 */
import type { ReactNode } from 'react';
import { gamingCovers, type CoverFiling } from '@cider/domain/engines';
import { formatDateTime } from '@cider/ui';
import { useLoad } from '../views/money/shared.js';
import { usePrinted } from './printed.js';
import { DrawnPages } from './drawn-doc.js';

interface Raffle {
  numbersFrom: number | null;
  numbersTo: number | null;
  drawingAt: string | null;
}

export function GamingCovers({ fy }: { fy: number }): ReactNode {
  // The festival a fiscal year closes is the one held the September before its June 30.
  const year = String(fy - 1);
  const filings = useLoad<{ cards: CoverFiling[] }>(`/money/state-filings/${year}`);
  const raffles = useLoad<{ rows: Raffle[] }>(`/marketing/raffles?year=${year}`);
  const sold = useLoad<{ rows: { ticketsSold: number }[] }>(
    `/marketing/raffles/filing?year=${year}`,
  );
  const loads = [filings, raffles, sold];
  const ready = loads.every((l) => l.data !== null || l.error !== null);
  usePrinted('doc-gaming-covers', ready, { year: fy - 1 });
  if (!ready) return <p>Reading the figures…</p>;
  const card = (kind: string): CoverFiling | null =>
    filings.data?.cards.find((c) => c.kind === kind) ?? null;
  const books = raffles.data?.rows ?? null;
  // Tickets printed are each raffle's numbers, first to last; a raffle without them is not counted.
  const printed = books?.every((r) => r.numbersFrom !== null && r.numbersTo !== null)
    ? books.reduce((n, r) => n + (r.numbersTo ?? 0) - (r.numbersFrom ?? 0) + 1, 0)
    : null;
  const last = (books ?? [])
    .map((r) => r.drawingAt)
    .filter((d): d is string => d !== null)
    .sort()
    .at(-1);
  const pages = gamingCovers({
    raffle: card('raffle'),
    bingo: card('bingo'),
    tickets:
      printed !== null && sold.data
        ? { printed, sold: sold.data.rows.reduce((n, r) => n + r.ticketsSold, 0) }
        : null,
    lastDrawing: last ? formatDateTime(last, { year: true }) : null,
  });
  return <DrawnPages pages={pages} size="half" />;
}
